/**
 * Phase 8.1 — Waiter and Due Out rollover proactive resolution.
 *
 * Doctrine:
 *   - Bucket 4.5 §4.5.1  Due Out is a hard customer-promise constraint.
 *   - Bucket 4.5 §4.5.5  Due Out outranks Waiter Flex; physical
 *                        constraints outrank Due Out.
 *   - Bucket 4.5 §4.5.9  Waiter Flex must NOT extend Due Out.
 *   - Bucket 5 §5.x      Rollover cannot silently violate customer
 *                        promises. Rolling work to the next day must
 *                        preserve Due Out OR require an authorized
 *                        Due Out edit / breach record.
 *   - Bucket 5 §5.x      If rollover threatens a Waiter promise, Core
 *                        must warn and require correction; Catalyst
 *                        may plan resolve/escalate actions but they
 *                        remain suggestion / manual-only unless an
 *                        existing safe resolver path is already
 *                        present and guarded.
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 8.1 confirmed:
 *
 *   - `eodRolloverHandler.ts:createEODRolloverSplit` (L168+)
 *     DEFENSIVELY refuses Waiter tickets at L193-196:
 *       "Refusing to rollover Waiter ticket — convert to Drop-off
 *        first or reassign."
 *     This is a hard block, not a proactive warning surface.
 *   - `validateDueOut(dueOut, optiFinishTime)` (L67-79) exists as a
 *     standalone validator returning `{valid, overshootMin}` — but is
 *     NOT auto-invoked inside `createEODRolloverSplit`. Callers must
 *     opt in.
 *   - 4-condition eligibility guard checks
 *     `actually_started`, `bay_id`, not `completed_at`, running OR
 *     paused-in-bay (L199-216).
 *   - No proactive PLANNER surfaces risk states + resolution actions
 *     BEFORE `createEODRolloverSplit` is invoked. This means
 *     callers (`useEODMonitor`, `EODRolloverModal`, future
 *     automation) must each independently decide whether to invoke
 *     rollover — and they may miss Due Out violations on Drop-off
 *     tickets (Waiter is already refused, but Drop-off with a
 *     tight Due Out can still create a continuation that misses
 *     the promise the next day).
 *
 * Phase 8.1 supplies:
 *   1. Canonical pure planner that takes a proposed rollover
 *      (parent ticket + Part B projected finish + optional ticket
 *      set) and returns the risk state + recommended resolution
 *      actions + blockers.
 *   2. Composes existing Phase 4.1 / 4.2 / 5.1 / 5.3 / 5.5 / 7.5 /
 *      7.7 / 7.9 helpers — Waiter risk via
 *      `getWaiterFlexRiskState`, Due Out authorization via
 *      `canEditDueOut`, descendant guard via
 *      `getRolloverDescendantSplitBlockers`, physical reality via
 *      `validateVehiclePhysicalReality`, bay lockout via
 *      `getBayPlacementEligibility`.
 *   3. Doctrinal invariants test-pinned: Due Out outranks Waiter,
 *      Waiter Flex never extends Due Out, all Catalyst actions are
 *      suggestion / manual-only.
 *
 * Phase 8.1 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections + the projected Part B finish.
 *   - No DB migration / no schema change.
 *   - No runtime wiring. `eodRolloverHandler.ts`, `useEODMonitor`,
 *     `EODRolloverModal`, cascade, smart-assign, AutoBump, board
 *     issue detector, Catalyst, and ticket movement paths are
 *     untouched.
 *
 * Builds on:
 *   - Phase 4.1 `waiterAnchorWarning.getWaiterFlexRiskState` /
 *     `isWaiterTicket`.
 *   - Phase 5.1 `dueOutEditAuthorization.canEditDueOut` /
 *     `classifyDueOutEdit`.
 *   - Phase 5.3 dueOutCorrectiveActionPlanner (suggestion-only
 *     actions for Due Out misses).
 *   - Phase 5.5 `dueOutSplitMergeValidation.validateDueOutForManualMerge`
 *     (`final_completion_after_due_out` precedent).
 *   - Phase 7.5 `rolloverDescendantGuard.getRolloverDescendantSplitBlockers`
 *     (`active_rollover_descendant_exists`).
 *   - Phase 7.7 `vehiclePhysicalReality.validateVehiclePhysicalReality`.
 *   - Phase 7.9 `bayLockoutEnforcement.getBayPlacementEligibility`
 *     (`bay_locked_out`).
 *   - Phase 1.1 `getProductionState` for terminal exclusion.
 */

import {
  isWaiterTicket,
  type WaiterTicketShape,
  type WaiterFlexRiskState,
} from './waiterAnchorWarning';
import {
  canEditDueOut,
  type DueOutEditAuthorizedRole,
} from './dueOutEditAuthorization';
import {
  getRolloverDescendantSplitBlockers,
  type RolloverDescendantShape,
  type RolloverDescendantSplitBlocker,
} from './rolloverDescendantGuard';
import {
  validateVehiclePhysicalReality,
  type VehiclePhysicalRealityShape,
  type PhysicalRealityBlocker,
} from './vehiclePhysicalReality';
import {
  getBayPlacementEligibility,
  type BayLockoutShape,
  type BayLockoutBlocker,
} from './bayLockoutEnforcement';
import {
  getProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import { isTicketDeleted } from '@/utils/ticketDeletedState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete rollover-promise risk states.
 *
 *   - 'safe'                                  — Part B projection
 *      respects Due Out + Waiter promise (or N/A).
 *   - 'waiter_promise_at_risk'               — Waiter ticket whose
 *      Part B projection puts the promise inside the flex window
 *      (anchor-warning). Core warns.
 *   - 'waiter_promise_violation'             — Waiter ticket whose
 *      Part B projection violates the flex window. Core REQUIRES
 *      correction (manual reassign / convert-to-drop-off /
 *      reschedule).
 *   - 'due_out_at_risk'                       — non-Waiter ticket
 *      whose Part B projection approaches Due Out (within an
 *      optional tolerance window). Core warns.
 *   - 'due_out_violation'                     — Part B projection
 *      exceeds Due Out. Requires authorized Due Out edit OR breach
 *      record.
 *   - 'authorized_due_out_change_required'    — emitted by the
 *      planner when the doctrinal path forward is an authorized
 *      Due Out edit (manager / org-admin override + reason).
 *   - 'breach_record_required'                — Due Out violation
 *      that cannot be avoided AND no authorized edit will be taken;
 *      a breach record must be filed.
 *   - 'manual_correction_required'           — Waiter violation or
 *      physical-constraint blocker that needs a human decision.
 *   - 'catalyst_resolution_available'         — Catalyst can plan
 *      a suggestion-only resolution (reshuffle, reassign,
 *      parking-to-bay acceleration, etc.).
 *   - 'blocked_physical_constraint'           — bay lockout /
 *      vehicle physical reality / active rollover descendant
 *      blocks the rollover regardless of promise status.
 *   - 'blocked_missing_context'               — caller did not pass
 *      enough context to resolve (no projected Part B finish, no
 *      Due Out on a non-Waiter ticket, etc.).
 */
export type RolloverPromiseRiskState =
  | 'safe'
  | 'waiter_promise_at_risk'
  | 'waiter_promise_violation'
  | 'due_out_at_risk'
  | 'due_out_violation'
  | 'authorized_due_out_change_required'
  | 'breach_record_required'
  | 'manual_correction_required'
  | 'catalyst_resolution_available'
  | 'blocked_physical_constraint'
  | 'blocked_missing_context';

export const ROLLOVER_PROMISE_RISK_STATES = Object.freeze([
  'safe',
  'waiter_promise_at_risk',
  'waiter_promise_violation',
  'due_out_at_risk',
  'due_out_violation',
  'authorized_due_out_change_required',
  'breach_record_required',
  'manual_correction_required',
  'catalyst_resolution_available',
  'blocked_physical_constraint',
  'blocked_missing_context',
] as const) satisfies ReadonlyArray<RolloverPromiseRiskState>;

/**
 * Discrete resolution actions emitted by the planner. ALL Catalyst
 * actions carry `manualOnly: true` semantics — the helper never
 * auto-resolves. Adoption code is responsible for routing the
 * action to a human reviewer / suggested workflow.
 */
export type RolloverResolutionAction =
  | 'core_warn_waiter_risk'
  | 'core_require_manual_correction'
  | 'catalyst_plan_reshuffle'
  | 'catalyst_plan_reassign'
  | 'catalyst_plan_parking_to_bay_acceleration'
  | 'catalyst_plan_split_or_merge_review'
  | 'catalyst_plan_authorized_due_out_edit'
  | 'catalyst_plan_breach_record'
  | 'block_rollover_until_resolved'
  | 'allow_rollover'
  | 'manual_only';

export const ROLLOVER_RESOLUTION_ACTIONS = Object.freeze([
  'core_warn_waiter_risk',
  'core_require_manual_correction',
  'catalyst_plan_reshuffle',
  'catalyst_plan_reassign',
  'catalyst_plan_parking_to_bay_acceleration',
  'catalyst_plan_split_or_merge_review',
  'catalyst_plan_authorized_due_out_edit',
  'catalyst_plan_breach_record',
  'block_rollover_until_resolved',
  'allow_rollover',
  'manual_only',
] as const) satisfies ReadonlyArray<RolloverResolutionAction>;

/**
 * Discrete blocker codes the planner emits. Composed from Phase
 * 7.5 / 7.7 / 7.9 vocabulary plus Phase 8.1-specific codes for
 * Waiter / Due Out / authorization signals.
 */
export type RolloverPromiseBlocker =
  | 'rollover_exceeds_waiter_promise'
  | 'rollover_exceeds_due_out'
  | 'due_out_edit_authorization_required'
  | 'breach_record_required'
  | 'waiter_flex_cannot_extend_due_out'
  | 'physical_constraint_blocks_resolution'
  | 'active_rollover_descendant_exists'
  | 'bay_lockout_blocks_rollover'
  | 'vehicle_physical_reality_conflict'
  | 'missing_waiter_promise'
  | 'missing_due_out'
  | 'missing_projected_completion'
  | 'missing_rollover_context';

export const ROLLOVER_PROMISE_BLOCKERS = Object.freeze([
  'rollover_exceeds_waiter_promise',
  'rollover_exceeds_due_out',
  'due_out_edit_authorization_required',
  'breach_record_required',
  'waiter_flex_cannot_extend_due_out',
  'physical_constraint_blocks_resolution',
  'active_rollover_descendant_exists',
  'bay_lockout_blocks_rollover',
  'vehicle_physical_reality_conflict',
  'missing_waiter_promise',
  'missing_due_out',
  'missing_projected_completion',
  'missing_rollover_context',
] as const) satisfies ReadonlyArray<RolloverPromiseBlocker>;

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Permissive ticket shape combining the fields the planner reads
 * across all composed helpers.
 */
export interface RolloverPromiseTicketShape
  extends ProductionStateTicketShape,
    WaiterTicketShape,
    VehiclePhysicalRealityShape,
    RolloverDescendantShape {
  /** Actual worked minutes so far (used by the long-EOD continuation math). */
  actual_minutes?: number | null;
  /** Customer-promise time (Due Out). */
  due_out?: string | Date | null;
  /** Free-form note about the rollover (audit). */
  rollover_reason?: string | null;
}

export interface RolloverPromiseResolutionInput {
  /**
   * The parent ticket being rolled over. Required.
   */
  parent: RolloverPromiseTicketShape;
  /**
   * Caller-supplied projected finish time for the Part B
   * continuation. Required — the planner's whole point is to
   * compare this projection against the parent's Due Out / Waiter
   * promise.
   */
  projectedPartBFinishAt?: Date | string | null;
  /**
   * Optional set of all tickets for physical-reality + active
   * descendant scans. When omitted, those checks are skipped.
   */
  tickets?: ReadonlyArray<RolloverPromiseTicketShape> | null;
  /**
   * Optional target bay (or candidate bay) the Part B continuation
   * would be placed in. When provided AND locked-out → emits
   * `bay_lockout_blocks_rollover`.
   */
  targetBay?: BayLockoutShape | null;
  /**
   * Optional acting user's role for Due Out edit authorization.
   * Required ONLY when the planner needs to recommend an
   * authorized Due Out edit. When missing AND a Due Out violation
   * is present → emits `due_out_edit_authorization_required`.
   */
  actorRole?: DueOutEditAuthorizedRole | string | null;
  /**
   * Optional tolerance (minutes) for `due_out_at_risk` state.
   * Default 30 min — Part B projected within 30 min of Due Out
   * triggers an at-risk warning.
   */
  dueOutAtRiskWindowMin?: number;
  /**
   * Optional Waiter flex minutes (shop-configured). Defaults to 0
   * — strict: any Part B finish past `scheduled_start_time` is a
   * violation. Pass the shop's actual Waiter flex setting for
   * realistic gating.
   */
  waiterFlexMin?: number;
  /**
   * Optional tolerance (minutes) for `waiter_promise_at_risk`.
   * Default 15 min — Part B projection within 15 min of the
   * promise (or flex boundary) triggers an at-risk warning.
   */
  waiterAtRiskWindowMin?: number;
  /**
   * Optional context hint: is the parent in a Waiter flex window
   * (caller-computed via Phase 4.1 `getWaiterFlexRiskState`). When
   * omitted, the planner derives it from the projected Part B
   * finish vs the Waiter promise baseline.
   */
  waiterRiskHint?: WaiterFlexRiskState | null;
}

// =============================================================================
// Internal helpers
// =============================================================================

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function isTerminalParent(t: RolloverPromiseTicketShape): boolean {
  if (isTicketDeleted(t) || t.status === 'Cancelled') return true;
  if (t.picked_up_at != null) return true;
  if (t.archived_at != null) return true;
  const state = getProductionState(t);
  return state === 'deleted' || state === 'archived' || state === 'picked_up';
}

// =============================================================================
// Verdict
// =============================================================================

export interface RolloverPromiseRiskVerdict {
  state: RolloverPromiseRiskState;
  /** Aggregate blocker list (Phase 7.5/7.7/7.9 piped through). */
  blockers: RolloverPromiseBlocker[];
  /** Recommended resolution actions; all Catalyst entries are manualOnly. */
  actions: RolloverResolutionAction[];
  /** True iff state is `safe`. */
  ok: boolean;
  /** Diagnostic: overshoot in minutes when Due Out or Waiter is violated. */
  overshootMin: number | null;
  /** Diagnostic: waiter flex state when applicable. */
  waiterRiskState: WaiterFlexRiskState | null;
}

// =============================================================================
// Public planner
// =============================================================================

/**
 * Build the canonical rollover-promise risk verdict + resolution
 * plan. Pure. Does not mutate inputs.
 *
 * Resolution order (the worst-case state wins; lower listed states
 * are not downgraded by milder later checks):
 *
 *   1. Terminal parent (picked_up/deleted/archived/Cancelled) → `safe`
 *      (nothing to roll over).
 *   2. Missing parent → `blocked_missing_context`.
 *   3. Missing `projectedPartBFinishAt` → `blocked_missing_context` +
 *      `missing_projected_completion`.
 *   4. Physical-reality conflict (vehicle in two bays / mixed) →
 *      `blocked_physical_constraint` + `vehicle_physical_reality_conflict`.
 *   5. Bay lockout on target bay → `blocked_physical_constraint` +
 *      `bay_lockout_blocks_rollover`.
 *   6. Active rollover descendant exists → `blocked_physical_constraint` +
 *      `active_rollover_descendant_exists`.
 *   7. Waiter ticket flex violation → `waiter_promise_violation` +
 *      `core_require_manual_correction`.
 *   8. Due Out violation (Part B finish > due_out) → `due_out_violation`.
 *      If no actor role / authorized edit context → emits
 *      `due_out_edit_authorization_required`; recommends
 *      `catalyst_plan_authorized_due_out_edit` + `catalyst_plan_breach_record`
 *      (suggestion-only).
 *   9. Waiter ticket anchor-warning → `waiter_promise_at_risk` +
 *      `core_warn_waiter_risk`.
 *  10. Non-Waiter ticket whose Part B finish is within
 *      `dueOutAtRiskWindowMin` of `due_out` → `due_out_at_risk` +
 *      `core_warn_waiter_risk` style warning (uses Core warn, not
 *      Catalyst).
 *  11. Otherwise → `safe` + `allow_rollover`.
 *
 * Bucket 4.5 §4.5.5 invariant: Due Out outranks Waiter — when both
 * fire, the verdict surfaces `due_out_violation` over
 * `waiter_promise_violation`. The planner emits BOTH blockers but
 * the canonical state field is the Due Out one.
 */
export function getRolloverPromiseResolutionPlan(
  input: RolloverPromiseResolutionInput,
): RolloverPromiseRiskVerdict {
  const blockers: RolloverPromiseBlocker[] = [];
  const actions: RolloverResolutionAction[] = [];

  if (!input.parent) {
    return {
      state: 'blocked_missing_context',
      blockers: ['missing_rollover_context'],
      actions: ['manual_only'],
      ok: false,
      overshootMin: null,
      waiterRiskState: null,
    };
  }

  // 1. Terminal parent → safe, no rollover.
  if (isTerminalParent(input.parent)) {
    return {
      state: 'safe',
      blockers,
      actions: ['allow_rollover'],
      ok: true,
      overshootMin: null,
      waiterRiskState: null,
    };
  }

  // 3. Projected Part B finish required.
  const projectedMs = parseTs(input.projectedPartBFinishAt);
  if (projectedMs == null) {
    pushUnique(blockers, 'missing_projected_completion');
    pushUnique(actions, 'manual_only');
    return {
      state: 'blocked_missing_context',
      blockers,
      actions,
      ok: false,
      overshootMin: null,
      waiterRiskState: null,
    };
  }

  // 4. Physical-reality conflicts across the broader ticket set.
  let physicalBlockerHit = false;
  if (Array.isArray(input.tickets) && input.tickets.length > 0) {
    const pr = validateVehiclePhysicalReality(
      input.tickets as ReadonlyArray<VehiclePhysicalRealityShape>,
    );
    if (!pr.ok && pr.blockers.length > 0) {
      // Phase 7.7 blockers piped through under a single Phase 8.1 code.
      pushUnique(blockers, 'vehicle_physical_reality_conflict');
      physicalBlockerHit = true;
    }
  }

  // 5. Bay lockout.
  if (input.targetBay != null) {
    const bayVerdict = getBayPlacementEligibility(input.targetBay, 'rollover');
    if (!bayVerdict.ok) {
      pushUnique(blockers, 'bay_lockout_blocks_rollover');
      physicalBlockerHit = true;
    }
  }

  // 6. Active rollover descendant.
  if (Array.isArray(input.tickets) && input.tickets.length > 0 && input.parent.id != null) {
    const descendantBlockers = getRolloverDescendantSplitBlockers(
      input.parent.id,
      input.tickets as ReadonlyArray<RolloverDescendantShape>,
    );
    if (
      descendantBlockers.includes('active_rollover_descendant_exists') ||
      descendantBlockers.includes('unresolved_rollover_chain')
    ) {
      pushUnique(blockers, 'active_rollover_descendant_exists');
      physicalBlockerHit = true;
    }
  }

  if (physicalBlockerHit) {
    pushUnique(actions, 'block_rollover_until_resolved');
    pushUnique(actions, 'manual_only');
    return {
      state: 'blocked_physical_constraint',
      blockers,
      actions,
      ok: false,
      overshootMin: null,
      waiterRiskState: null,
    };
  }

  // Waiter + Due Out math.
  const isWaiter = isWaiterTicket(input.parent);
  const dueOutMs = parseTs(input.parent.due_out);
  let overshootMin: number | null = null;

  // 8. Due Out violation (outranks Waiter per §4.5.5).
  let dueOutViolation = false;
  let dueOutAtRisk = false;
  if (dueOutMs != null) {
    if (projectedMs > dueOutMs) {
      dueOutViolation = true;
      overshootMin = Math.round((projectedMs - dueOutMs) / 60_000);
      pushUnique(blockers, 'rollover_exceeds_due_out');
    } else {
      // Within tolerance window?
      const tol = (input.dueOutAtRiskWindowMin ?? 30) * 60_000;
      if (projectedMs >= dueOutMs - tol) dueOutAtRisk = true;
    }
  }

  // 7. Waiter ticket risk.
  //
  // Doctrine (Phase 4.1 + Phase 8.1): a Waiter ticket's "promise"
  // is `scheduled_start_time` (the appointment baseline). Phase
  // 8.1's check: does Part B projection extend past that promise
  // (plus optional shop flex)?
  //
  //   - projectedMs > scheduled_start_time + flex → flex_violation
  //   - projectedMs within waiterAtRiskWindowMin of scheduled_start_time
  //     + flex → anchor_warning
  //   - else → no_risk
  //
  // `eodRolloverHandler.ts:193-196` defensively REFUSES Waiter
  // rollover entirely. Phase 8.1's planner mirrors that doctrine
  // by flagging the violation proactively so callers can surface
  // the suggested manual-correction paths (reassign, convert to
  // Drop-off, reschedule) BEFORE the runtime block fires.
  let waiterRiskState: WaiterFlexRiskState | null = null;
  let waiterViolation = false;
  let waiterAtRisk = false;
  if (isWaiter) {
    const waiterPromiseMs = parseTs(input.parent.scheduled_start_time);
    if (waiterPromiseMs == null) {
      // Waiter without a scheduled_start_time = data integrity gap.
      pushUnique(blockers, 'missing_waiter_promise');
    } else {
      const flexMin = input.waiterFlexMin ?? 0;
      const flexEndMs = waiterPromiseMs + flexMin * 60_000;
      const atRiskMs = (input.waiterAtRiskWindowMin ?? 15) * 60_000;

      if (input.waiterRiskHint != null) {
        waiterRiskState = input.waiterRiskHint;
      } else if (projectedMs > flexEndMs) {
        waiterRiskState = 'flex_violation';
      } else if (projectedMs >= flexEndMs - atRiskMs) {
        waiterRiskState = 'anchor_warning';
      } else {
        waiterRiskState = 'no_risk';
      }

      if (waiterRiskState === 'flex_violation') {
        waiterViolation = true;
        pushUnique(blockers, 'rollover_exceeds_waiter_promise');
      } else if (waiterRiskState === 'anchor_warning') {
        waiterAtRisk = true;
      }
    }
  } else if (dueOutMs == null) {
    // Non-Waiter ticket with no Due Out — flag context gap.
    pushUnique(blockers, 'missing_due_out');
  }

  // 8a. Due Out violation handling (must outrank Waiter).
  if (dueOutViolation) {
    // Phase 4.5 §4.5.9: Waiter Flex CANNOT extend Due Out. Emit the
    // doctrinal invariant blocker too so audit shows both reasons.
    if (isWaiter) pushUnique(blockers, 'waiter_flex_cannot_extend_due_out');

    // Authorization path.
    const actor = input.actorRole;
    const canAuthorize =
      actor != null &&
      typeof actor === 'string' &&
      canEditDueOut(actor as DueOutEditAuthorizedRole);
    if (!canAuthorize) {
      pushUnique(blockers, 'due_out_edit_authorization_required');
      pushUnique(actions, 'catalyst_plan_authorized_due_out_edit');
      pushUnique(actions, 'catalyst_plan_breach_record');
      pushUnique(actions, 'block_rollover_until_resolved');
      pushUnique(actions, 'manual_only');
      return {
        state: 'authorized_due_out_change_required',
        blockers,
        actions,
        ok: false,
        overshootMin,
        waiterRiskState,
      };
    }

    // Actor CAN authorize → still suggestion-only; planner does NOT
    // auto-edit. Surfaces the two doctrinal options.
    pushUnique(actions, 'catalyst_plan_authorized_due_out_edit');
    pushUnique(actions, 'catalyst_plan_breach_record');
    pushUnique(actions, 'manual_only');
    return {
      state: 'due_out_violation',
      blockers,
      actions,
      ok: false,
      overshootMin,
      waiterRiskState,
    };
  }

  // 7a. Waiter violation (only reaches here when Due Out is OK or absent).
  if (waiterViolation) {
    pushUnique(actions, 'core_require_manual_correction');
    pushUnique(actions, 'catalyst_plan_reshuffle');
    pushUnique(actions, 'catalyst_plan_reassign');
    pushUnique(actions, 'catalyst_plan_parking_to_bay_acceleration');
    pushUnique(actions, 'catalyst_plan_split_or_merge_review');
    pushUnique(actions, 'block_rollover_until_resolved');
    pushUnique(actions, 'manual_only');
    return {
      state: 'waiter_promise_violation',
      blockers,
      actions,
      ok: false,
      overshootMin,
      waiterRiskState,
    };
  }

  // 9. Waiter at-risk.
  if (waiterAtRisk) {
    pushUnique(actions, 'core_warn_waiter_risk');
    pushUnique(actions, 'catalyst_plan_reshuffle');
    pushUnique(actions, 'catalyst_plan_reassign');
    pushUnique(actions, 'manual_only');
    return {
      state: 'waiter_promise_at_risk',
      blockers,
      actions,
      ok: false,
      overshootMin,
      waiterRiskState,
    };
  }

  // 10. Due Out at-risk (non-Waiter only — Waiter already handled).
  if (dueOutAtRisk) {
    pushUnique(actions, 'core_warn_waiter_risk');
    pushUnique(actions, 'catalyst_plan_reshuffle');
    pushUnique(actions, 'catalyst_plan_reassign');
    pushUnique(actions, 'catalyst_plan_parking_to_bay_acceleration');
    pushUnique(actions, 'manual_only');
    return {
      state: 'due_out_at_risk',
      blockers,
      actions,
      ok: false,
      overshootMin,
      waiterRiskState,
    };
  }

  // 11. Default: safe.
  pushUnique(actions, 'allow_rollover');
  return {
    state: 'safe',
    blockers,
    actions,
    ok: true,
    overshootMin,
    waiterRiskState,
  };
}

// =============================================================================
// Convenience wrappers
// =============================================================================

/**
 * Boolean: does the proposed rollover preserve Due Out?
 * Sister to `getRolloverPromiseResolutionPlan` for callers that
 * only want the Due Out gate.
 */
export function validateRolloverPreservesDueOut(
  input: RolloverPromiseResolutionInput,
): { ok: boolean; overshootMin: number | null; blockers: RolloverPromiseBlocker[] } {
  const plan = getRolloverPromiseResolutionPlan(input);
  const due_out_blockers = plan.blockers.filter(
    b =>
      b === 'rollover_exceeds_due_out' ||
      b === 'due_out_edit_authorization_required' ||
      b === 'breach_record_required' ||
      b === 'waiter_flex_cannot_extend_due_out' ||
      b === 'missing_due_out' ||
      b === 'missing_projected_completion',
  );
  return {
    ok:
      plan.state !== 'due_out_violation' &&
      plan.state !== 'authorized_due_out_change_required' &&
      plan.state !== 'breach_record_required' &&
      plan.state !== 'blocked_missing_context',
    overshootMin: plan.overshootMin,
    blockers: due_out_blockers,
  };
}

/**
 * Boolean: does the proposed rollover preserve the Waiter promise?
 */
export function validateRolloverPreservesWaiterPromise(
  input: RolloverPromiseResolutionInput,
): { ok: boolean; waiterRiskState: WaiterFlexRiskState | null; blockers: RolloverPromiseBlocker[] } {
  const plan = getRolloverPromiseResolutionPlan(input);
  const waiterBlockers = plan.blockers.filter(
    b =>
      b === 'rollover_exceeds_waiter_promise' ||
      b === 'missing_waiter_promise' ||
      b === 'waiter_flex_cannot_extend_due_out',
  );
  return {
    ok:
      plan.state !== 'waiter_promise_violation' &&
      plan.state !== 'waiter_promise_at_risk',
    waiterRiskState: plan.waiterRiskState,
    blockers: waiterBlockers,
  };
}

/** Flat blocker list. */
export function getRolloverPromiseBlockers(
  input: RolloverPromiseResolutionInput,
): RolloverPromiseBlocker[] {
  return getRolloverPromiseResolutionPlan(input).blockers;
}

/** Flat action list. */
export function getRolloverProactiveResolutionActions(
  input: RolloverPromiseResolutionInput,
): RolloverResolutionAction[] {
  return getRolloverPromiseResolutionPlan(input).actions;
}

/**
 * Single-call risk state. Sister to `getRolloverPromiseResolutionPlan`
 * for callers that only want the state field.
 */
export function getRolloverPromiseRiskState(
  input: RolloverPromiseResolutionInput,
): RolloverPromiseRiskState {
  return getRolloverPromiseResolutionPlan(input).state;
}

/** Boolean: is the parent a Waiter at flex risk on its current Part B projection? */
export function getWaiterRolloverRiskState(
  input: RolloverPromiseResolutionInput,
): WaiterFlexRiskState | null {
  return getRolloverPromiseResolutionPlan(input).waiterRiskState;
}

/** Phase 4.5 §4.5.5 doctrinal invariant — Due Out outranks Waiter. */
export function getDueOutRolloverRiskState(
  input: RolloverPromiseResolutionInput,
): RolloverPromiseRiskState {
  const plan = getRolloverPromiseResolutionPlan(input);
  // When both Due Out and Waiter fire, the planner already returns
  // the Due Out state. This wrapper exists so callers can ask the
  // doctrinal question directly.
  if (plan.state === 'waiter_promise_violation' || plan.state === 'waiter_promise_at_risk') {
    return 'safe';
  }
  return plan.state;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. **useEODMonitor / EODRolloverModal preflight**:
//    Before invoking `createEODRolloverSplit`, call
//    `getRolloverPromiseResolutionPlan({parent, projectedPartBFinishAt,
//    tickets, targetBay, actorRole})` and:
//      - `state === 'safe'` → proceed.
//      - `state === 'waiter_promise_at_risk' / 'due_out_at_risk'` →
//        render Core warning banner; allow user to acknowledge +
//        proceed.
//      - `state === 'waiter_promise_violation'` → block + render
//        the suggested manual-correction options.
//      - `state === 'authorized_due_out_change_required'` → block +
//        offer authorized Due Out edit OR breach-record flow.
//      - `state === 'blocked_physical_constraint'` → block + route
//        to the appropriate Phase 7.5/7.7/7.9 helper UI for
//        resolution.
//
// 2. **Catalyst optimizer preflight**:
//    The autonomous optimizer can call this planner with the
//    full ticket set and refuse to propose a rollover plan when
//    the resulting state is not `safe` or `due_out_at_risk`. All
//    Catalyst actions in the verdict are manualOnly — the
//    optimizer surfaces them as suggestions to the writer/manager.
//
// 3. **Board-issue detector**:
//    A future `ROLLOVER_PROMISE_RISK` board issue type can fire
//    when a scheduled ticket's projection puts Part B beyond
//    Due Out (per `dueOutRiskEligibility`) AND `useEODMonitor`
//    confirms EOD will trigger rollover. Suggestion-only routing
//    to Catalyst preserves the manual gate.
//
// 4. **Authorized Due Out edit / breach record**:
//    When `actorRole` is passed and `canEditDueOut(actorRole) ===
//    true`, the planner still emits `catalyst_plan_authorized_due_out_edit`
//    + `catalyst_plan_breach_record` as suggestion-only options.
//    The runtime DOES NOT auto-edit Due Out — adoption must route
//    the user through the existing Phase 5.1 `validateDueOutEdit`
//    + `buildDueOutEditAuditPayload` flow.
//
// 5. **Phase 8.2 follow-on**:
//    Run-long EOD continuation flow can compose this planner with
//    `afterHoursConfirmationEscalation` to provide an end-to-end
//    "should this ticket run past EOD, roll over, or stop" verdict.
//    Out of Phase 8.1 scope.
//
// 6. **Audit**:
//    Reserve `ROLLOVER_BLOCKED_WAITER_PROMISE` and
//    `ROLLOVER_REQUIRED_AUTHORIZED_DUE_OUT_EDIT` in
//    `AdminActionType` when adoption emits audit rows.
